/**
 * Exam Store - Runtime state management
 */

import { create } from 'zustand';
import type { ExamForm, CandidateExam, CandidateAnswer, ItemResponse } from '../types/exam';

interface ExamState {
  // Current exam session
  candidateExam: CandidateExam | null;
  examForm: ExamForm | null;

  // Answers
  answers: Record<string, CandidateAnswer>;

  // Timing
  serverTimeOffset: number; // client - server
  startedAt: number | null;
  timeRemaining: number | null;

  // UI state
  currentItemIndex: number;
  isSubmitting: boolean;
  autosaveStatus: 'idle' | 'saving' | 'saved' | 'error';

  // Actions
  initExam: (candidateExam: CandidateExam, examForm: ExamForm) => void;
  setAnswer: (itemId: string, response: ItemResponse) => void;
  setCurrentItem: (index: number) => void;
  setAutosaveStatus: (status: ExamState['autosaveStatus']) => void;
  updateTimeRemaining: (seconds: number) => void;
  startExam: () => void;
  reset: () => void;
}

export const useExamStore = create<ExamState>((set, get) => ({
  candidateExam: null,
  examForm: null,
  answers: {},
  serverTimeOffset: 0,
  startedAt: null,
  timeRemaining: null,
  currentItemIndex: 0,
  isSubmitting: false,
  autosaveStatus: 'idle',

  initExam: (candidateExam, examForm) => {
    const serverTimeOffset = Date.now() - candidateExam.server_time;
    set({
      candidateExam,
      examForm,
      serverTimeOffset,
      answers: {},
      currentItemIndex: 0,
    });
  },

  setAnswer: (itemId, response) => {
    const timestamp = Date.now() - get().serverTimeOffset;
    set(state => ({
      answers: {
        ...state.answers,
        [itemId]: {
          item_id: itemId,
          response,
          timestamp,
        },
      },
    }));
  },

  setCurrentItem: index => {
    set({ currentItemIndex: index });
  },

  setAutosaveStatus: status => {
    set({ autosaveStatus: status });
  },

  updateTimeRemaining: seconds => {
    set({ timeRemaining: seconds });
  },

  startExam: () => {
    const serverTime = Date.now() - get().serverTimeOffset;
    set({ startedAt: serverTime });
  },

  reset: () => {
    set({
      candidateExam: null,
      examForm: null,
      answers: {},
      serverTimeOffset: 0,
      startedAt: null,
      timeRemaining: null,
      currentItemIndex: 0,
      isSubmitting: false,
      autosaveStatus: 'idle',
    });
  },
}));
